import {
  ApartmentIcon,
  AssignmentIcon,
  BellIcon,
  BookIcon,
  BoxIcon,
  EditNoteIcon,
  GroupIcon,
  HandshakeIcon,
  InfoIcon,
  ListIcon,
  MapPinIcon,
  PersonIcon,
  RadarIcon,
  ShieldIcon,
  SirenIcon,
  SparkleIcon,
  WorkIcon,
} from '@/components/icons/registry';
import type { IconComponent } from '@/components/icons/types';

import { PLAYGROUND_MENU_ITEM_ENABLED } from './features';

/**
 * Itens da barra de navegação inferior.
 *
 * **Composição decidida em 2026-07-22**, sobre a visão v2 (substitui tanto o
 * mockup quanto o modelo de "Menu por perfil" das regras do app original —
 * este último dependia de papel-por-conta, que a v2 eliminou):
 *
 * `Mapa · Avisos · SOS · Pedidos · Perfil` — e, desde 2026-10, `Menu` no lugar de
 * `Perfil` (ver o fim deste bloco).
 *
 * - **Mapa** tem aba porque é o centro do produto (P3) — sem ela, quem saía do
 *   mapa ficava sem caminho de volta.
 * - **Avisos** funde alertas (broadcast) e notificações (pessoais) numa única
 *   entrada com segmentos — semânticas separadas dentro, um só lugar fora.
 *   "Conteúdo" (guias estáticos) saiu da barra; entra em outra superfície.
 * - **SOS** mantém o destaque central: app de resposta a desastre, botão de
 *   pânico não pode precisar ser procurado (DV-13).
 * - **Pedidos** é o par de produto do SOS (pedir ↔ atender) e pertence ao
 *   próximo módulo do roteiro; estreia como placeholder para não nascer 404.
 *
 * A barra aparece só em telas-destino: fluxos de tarefa (criar/gerenciar
 * instituição, futuro fluxo SOS) montam fora do `AppLayout`, sem barra — toque
 * acidental numa aba no meio de um formulário descarta o trabalho da pessoa.
 *
 * **A barra não é a única superfície de navegação** (decidido em 2026-07-23,
 * `plans/plano-perfil-e-navegacao.md` DP-01): cinco slots não comportam um
 * módulo novo a cada entrega, e disputar um deles empurraria para fora algo que
 * já se justificou. Os módulos entram pelo `MENU_ITEMS` abaixo — é a "outra
 * superfície" que o parágrafo de Avisos já prometia para o conteúdo estático.
 * A composição da barra **não muda** por causa disso.
 *
 * **Menu e Perfil trocaram de lugar** (planilha de tarefas, 2026-10):
 *
 * > "o botão do menu vai ficar onde está o botão do perfil, e o botão do
 * > perfil vai para cima, onde estava o botão do menu"
 *
 * A última célula da barra agora abre o menu (`AppMenuTab`, que não é rota e
 * por isso não está nesta lista), e o perfil é o botão do cabeçalho das telas —
 * o slot `actions` do `PageHeader` e o quadrado ao lado da busca no mapa
 * (`ProfileButton`, a partir de `PROFILE_ITEM`). Faz sentido além da ordem: o
 * menu é o que se percorre com o polegar, e a lista dele já subia do rodapé.
 */
export interface NavigationItem {
  /** Rota absoluta. */
  to: string;
  label: string;
  icon: IconComponent;
  /**
   * Item de destaque central. Hoje só o Socorro — é a ação que a pessoa precisa
   * achar sem procurar.
   */
  emphasis?: boolean;
}

const REQUESTS_ROUTE = '/requests';

export const NAVIGATION_ITEMS: NavigationItem[] = [
  { to: '/', label: 'Mapa', icon: MapPinIcon },
  { to: '/alerts', label: 'Avisos', icon: BellIcon },
  // Sem ícone de biblioteca: o SOS é autoral e recebe tratamento visual próprio
  // na barra. O ícone aqui é só o fallback de acessibilidade.
  { to: '/help-request', label: 'Socorro', icon: SirenIcon, emphasis: true },
  { to: REQUESTS_ROUTE, label: 'Pedidos', icon: HandshakeIcon },
];

/**
 * O perfil, que saiu da barra e virou o botão do cabeçalho (ver acima).
 *
 * Continua declarado como item de navegação — rota, rótulo e ícone num lugar
 * só — porque o `ProfileButton` e `DESTINATION_ROUTES` precisam dos três, e uma
 * segunda cópia de `'/profile'` nasceria desalinhada.
 */
export const PROFILE_ITEM: NavigationItem = { to: '/profile', label: 'Perfil', icon: PersonIcon };

export interface MenuItem extends NavigationItem {
  /** Linha de apoio no menu: diz o que a pessoa encontra do outro lado. */
  description: string;
}

/**
 * Itens do menu (o hambúrguer), onde os **módulos** do app entram.
 *
 * A régua de entrada é a mesma da barra: **só rota que existe**. Um item que
 * leva a "Página não encontrada" é pior que a ausência dele — ensina que o menu
 * mente e a pessoa para de abri-lo. **Reencontro entrou** quando o módulo passou
 * a existir (2026-08-16) — e o rótulo virou **"Buscar abrigados"** em 2026-10,
 * a pedido da planilha (o porquê está junto do item); **"Sobre" entrou** na mesma entrega que criou
 * `/about` e as três seções dele (2026-09-20) — avaliação não tem item porque não tem tela própria:
 * ela mora dentro da gestão de membros da instituição.
 *
 * Seis módulos entraram por aqui sem que a barra cedesse um slot — que era
 * exatamente a aposta da decisão de 2026-07-23. Vale notar que Conteúdo e
 * Doações são **públicos**: o menu aparece para convidado, e mandar quem quer
 * doar ou ler um guia fazer login antes seria perder os dois.
 *
 * O bloco de notas é o oposto e continua entrando: a nota mora em `user_notes`
 * sob o `profile_id` da pessoa, então sem sessão não há onde gravá-la. O item
 * leva ao login com `?next=/notes` — o mesmo que já acontece com Chamados, e
 * bem melhor que esconder do convidado um lugar que existe para ele assim que
 * criar a conta.
 *
 * O último item, o playground, é **andaime com prazo** — a explicação está
 * junto dele, mais abaixo.
 */
/**
 * ## A ordem é do produto, e não alfabética nem por data de entrada
 *
 * > "Reordenar o menu - [Instituições, Chamados, Doações, Reencontros,
 * > Conteúdos, Bloco de notas, Sobre, Administrador]"
 * > — planilha de tarefas, 2026-09-20
 *
 * Ela desce do que a pessoa **faz** para o que ela **lê**: instituição, vagas e
 * doações são participação; reencontro é busca; conteúdo e "Sobre" são leitura;
 * o bloco de notas é pessoal e fica entre os dois. "Administrador" fecha a
 * lista, fora daqui (ele é condicional — ver `PLATFORM_MENU_ITEM`).
 *
 * ⚠️ **"Chamados" virou "Mural de vagas", e mudou de destino junto**: a mesma
 * rodada da planilha desligou a convocação (`VOLUNTEER_CALLS_ENABLED`) e pôs a
 * vaga no lugar dela. O item ocupa a mesma posição que a linha pediu, com o
 * rótulo e a rota da entidade que ficou.
 *
 * ⚠️ Os plurais da linha ("Reencontros", "Conteúdos") não foram aplicados: são
 * a forma como o produto escreveu a lista, e os dois rótulos já publicados estão
 * no singular em todo lugar do app — inclusive no `<PageHeader>` das próprias
 * telas. O que a linha decide é a **ordem**.
 * ("Reencontro" depois virou "Buscar abrigados", por outra linha da planilha —
 * a posição na ordem é a mesma.)
 */
const MODULE_MENU_ITEMS: MenuItem[] = [
  {
    to: '/institutions',
    // Para a conta comum. A conta dona de instituição vê "Minha instituição" e
    // abre direto o perfil dela (`getMenuItems`), e a do QG ganha "Meus pedidos
    // SOS" — o item abaixo é a forma base, que vale para todas as outras.
    label: 'Instituições que participo',
    icon: ApartmentIcon,
    description: 'Onde você voluntaria e suas candidaturas',
  },
  {
    // A lista de todas as instituições cadastradas (planilha, 2026-10-31, T06).
    // A página é do módulo de instituições; aqui só a porta.
    to: '/institutions/directory',
    label: 'Lista de instituições',
    icon: ListIcon,
    description: 'Abrigos, QGs e centros de distribuição, perto de você',
  },
  {
    to: '/openings',
    label: 'Mural de vagas',
    // Ícone próprio (T07): o aperto de mãos já era o de "Pedidos" na barra, e
    // dois destinos diferentes com o mesmo desenho ensinavam a confundir os dois.
    icon: WorkIcon,
    description: 'Vagas de voluntariado abertas nas instituições',
  },
  {
    to: '/donations',
    label: 'Quero doar',
    icon: BoxIcon,
    description: 'O que as instituições estão precisando',
  },
  {
    to: '/reunion',
    // "Buscar abrigados", e não "Reencontro" (planilha, 2026-10): o rótulo diz
    // o que a pessoa faz ali em vez de nomear o módulo. Só o rótulo mudou — a
    // rota e o módulo seguem `reunion`, e o título da própria tela acompanha
    // para que o toque no menu não caia numa página de outro nome.
    label: 'Buscar abrigados',
    icon: GroupIcon,
    description: 'Procure pelo nome quem já está registrado em um abrigo',
  },
  {
    to: '/content',
    // "Informações" (planilha, 2026-10-31, T11): a rota e o módulo seguem
    // `content`, só o nome que a pessoa lê mudou.
    label: 'Informações',
    icon: BookIcon,
    description: 'Guias, telefones de emergência e links úteis',
  },
  {
    to: '/notes',
    label: 'Bloco de notas',
    icon: EditNoteIcon,
    // A descrição carrega a fronteira de privacidade, e não é enfeite: é o que
    // a pessoa lê **antes** de decidir escrever ali o telefone de um vizinho.
    description: 'Suas anotações — ninguém mais vê',
  },
  {
    to: '/about',
    label: 'Sobre',
    icon: InfoIcon,
    description: 'Termos de uso, perguntas frequentes e contato',
  },
];

/**
 * ⏳ **Item temporário, para o time de design.**
 *
 * O playground não é um módulo do produto: é a bancada de testes visuais
 * (tema, ícones, marca, legenda do mapa, animações). Ele existia só por URL
 * digitada, o que serve para quem escreve o código e não para quem precisa
 * **ver** o resultado. Enquanto o design estiver acompanhando as telas de
 * perto, ele ganha porta de entrada no menu.
 *
 * A descrição diz o que é em vez de vender o lugar: quem abrir precisa entender
 * na primeira linha que aquilo não é funcionalidade do app.
 *
 * ⚠️ **Entra também no build publicado**, por decisão de 2026-07-23: o design
 * acompanha pelo endereço da Cloudflare, não por `pnpm dev`, e um item que só
 * existisse localmente não resolveria nada. O gate `import.meta.env.DEV` das
 * rotas foi levantado no mesmo commit (ver `modules/playground/routes.tsx`).
 *
 * **Quando tirar:** assim que o acompanhamento de design deixar de precisar
 * disso — some este item **e** devolve o gate das rotas. É item de andaime; não
 * vire mais um módulo por inércia.
 *
 * 🔻 **Fora do menu desde 2026-09-20**, atrás de `PLAYGROUND_MENU_ITEM_ENABLED`
 * (`config/features.ts`). O item segue declarado aqui — e não apagado — porque
 * a rota continua existindo: se a porta voltar, ela volta com este texto, e não
 * com uma descrição reescrita de memória.
 */
const PLAYGROUND_MENU_ITEM: MenuItem = {
  to: '/playground',
  label: 'Playground',
  icon: SparkleIcon,
  description: 'Coleção de testes visuais do time — não é funcionalidade do app',
};

export const MENU_ITEMS: MenuItem[] = PLAYGROUND_MENU_ITEM_ENABLED
  ? [...MODULE_MENU_ITEMS, PLAYGROUND_MENU_ITEM]
  : MODULE_MENU_ITEMS;

/**
 * O que a barra e o menu precisam saber da conta para se montar (T15, T26).
 * Vem de `useInstitutionRoles` — a config só decide o que fazer com a resposta.
 */
export interface NavigationContext {
  /** O QG de que a conta participa, se houver: ele troca um slot da barra. */
  hqInstitutionId?: string | null;
  /** A instituição de que a conta é dona, se houver: o item de menu abre o perfil dela. */
  ownedInstitutionId?: string | null;
}

/**
 * A barra de uma **conta de QG** (planilha, 2026-10-31, T26): "Pedidos" cede o
 * slot a um atalho para o mapa da operação, e "Meus pedidos SOS" passa para o
 * menu (`getMenuItems`). O rótulo é curto porque a célula tem ~74px; o título da
 * tela é "Operações de resgate".
 */
export function getNavigationItems({ hqInstitutionId }: NavigationContext = {}): NavigationItem[] {
  if (!hqInstitutionId) return NAVIGATION_ITEMS;

  return NAVIGATION_ITEMS.map((item) =>
    item.to === REQUESTS_ROUTE
      ? { to: `/institutions/${hqInstitutionId}/operation`, label: 'Operações', icon: RadarIcon }
      : item,
  );
}

/**
 * O menu da conta:
 *
 * - **Dona de instituição** (T15): "Instituições que participo" vira "Minha
 *   instituição" e abre **direto** o perfil dela, onde estão os botões de gestão.
 * - **Conta de QG** (T26): ganha "Meus pedidos SOS", que saiu da barra.
 */
export function getMenuItems({
  hqInstitutionId,
  ownedInstitutionId,
}: NavigationContext = {}): MenuItem[] {
  const items = MENU_ITEMS.map((item) =>
    item.to === '/institutions' && ownedInstitutionId
      ? {
          ...item,
          to: `/institutions/${ownedInstitutionId}`,
          label: 'Minha instituição',
          description: 'Gestão, vagas e voluntários da sua instituição',
        }
      : item,
  );

  if (!hqInstitutionId) return items;

  const ownRequests: MenuItem = {
    to: REQUESTS_ROUTE,
    label: 'Meus pedidos SOS',
    // Ícone próprio: a sirene já é a do SOS na barra, e dois destinos com o
    // mesmo desenho eram o que o T07 mandou tirar.
    icon: AssignmentIcon,
    description: 'Os pedidos de socorro que você fez',
  };
  const notesAt = items.findIndex((item) => item.to === '/notes');

  return notesAt === -1
    ? [...items, ownRequests]
    : [...items.slice(0, notesAt), ownRequests, ...items.slice(notesAt)];
}

/**
 * A porta da **plataforma** — o painel central em `/admin`.
 *
 * Fica **fora** de `MENU_ITEMS` de propósito: o `AppMenu` renderiza aquela lista
 * inteira, sem condição, e este item só existe para quem `is_platform_admin()`
 * reconhece. Quem monta a condição é o menu, com `usePlatformAdmin`.
 *
 * Até o plano do painel do admin (2026-08-27) isto apontava direto para
 * `/admin/reports`: não havia painel, só a fila de denúncias. Agora `/admin` é
 * o hub — cartões para denúncias, contas (com bloqueio), pins, alertas,
 * conteúdo e pedidos de resgate (`modules/platform/lib/admin-sections.ts`) —,
 * e este item leva para lá.
 *
 * ⚠️ **O rótulo virou "Administrador" em 2026-09-20**, pela ordem que a planilha
 * pediu para o menu — é o nome que ela usa para este item. Na passada de UX
 * (2026-10) o produto fechou: **"Administrador" em tudo que o usuário lê** — o
 * menu, a descrição e as telas da conta admin. "Plataforma" fica só no código
 * (a tabela `platform_admins`, `is_platform_admin()`, `modules/platform`): quem
 * lê o menu vê um nome só, e quem lê o código continua achando o conceito pelo
 * nome de sempre.
 */
export const PLATFORM_MENU_ITEM: MenuItem = {
  to: '/admin',
  label: 'Administrador',
  icon: ShieldIcon,
  description: 'Painel de administração do Arcus',
};

/**
 * Rota da Home. O mapa é a tela inicial após login, cadastro e entrada como
 * convidado.
 */
export const HOME_ROUTE = '/';

/**
 * **Telas-destino: o app inteiro cabe num único nível de histórico.**
 *
 * O menu e a barra inferior navegavam com `push`, que é o padrão do `<Link>`.
 * Isso empilhava uma entrada por toque: alternar Instituições ↔ Conteúdo dez
 * vezes deixava dez entradas, e o "voltar" percorria a pilha tela por tela em
 * vez de sair. É o defeito #1 do bug bash (2026-08-06).
 *
 * A correção é tratar destino como **aba**: sair de um destino para outro
 * `replace` a entrada atual, então o nível de destinos nunca cresce — que é
 * como barra de abas de app nativo se comporta.
 *
 * ⚠️ **Trade-off, e ele é real.** O "voltar" a partir de uma tela-destino leva
 * ao que existia **antes** de a pessoa entrar no nível de destinos (a tela de
 * entrada, ou o fechamento do PWA) — e **não** à aba anterior. Foi aceito
 * porque é o que a barra de abas ensina a esperar, e porque é a única versão
 * que elimina a pilha sem rastrear histórico à mão: a alternativa ("empilha a
 * primeira, substitui as seguintes") exige um espelho do histórico no cliente e
 * quebra no primeiro back/forward do navegador. "Voltar sempre cai no Mapa"
 * seria uma terceira decisão, de produto — não está implementada aqui.
 *
 * **A lista é derivada, nunca redigitada.** Item novo em `NAVIGATION_ITEMS` ou
 * em `MENU_ITEMS` vira destino sozinho; uma cópia manual das rotas nasceria
 * desatualizada na primeira entrega.
 *
 * Duas exclusões importam:
 *
 * - **O item de destaque (o SOS) fica de fora.** Ele não é destino: é fluxo de
 *   tarefa e monta **fora** do `AppLayout` (`routes/index.tsx`), como
 *   `/institutions/new`. Se ele substituísse a entrada atual, o "voltar" de
 *   dentro do socorro jogaria a pessoa para fora do app em vez de devolvê-la à
 *   tela de onde ela pediu ajuda.
 * - **Rota de detalhe nunca é destino**, porque a comparação é exata:
 *   `/institutions` é aba, `/institutions/abc` é detalhe e continua empilhando
 *   — ali o "voltar" precisa mesmo voltar para a lista.
 */
export const DESTINATION_ROUTES: readonly string[] = [
  ...NAVIGATION_ITEMS.filter((item) => !item.emphasis).map((item) => item.to),
  PROFILE_ITEM.to,
  ...MODULE_MENU_ITEMS.map((item) => item.to),
  // ⚠️ Os dois itens condicionais entram **sempre**, e por isso a lista não
  // consome `MENU_ITEMS`: o que a bandeira e o `is_platform_admin()` decidem é
  // quem **vê a porta** no menu, não o que é tela-destino. O playground segue
  // acessível por URL digitada e a fila da plataforma por link; fora daqui, as
  // duas empilhariam histórico e o "voltar" percorreria as telas anteriores uma
  // a uma — que é o defeito que esta lista existe para não ter.
  PLAYGROUND_MENU_ITEM.to,
  PLATFORM_MENU_ITEM.to,
];

const OPERATION_ROUTE_PATTERN = /^\/institutions\/[^/]+\/operation$/;

const DESTINATION_ROUTE_SET = new Set(DESTINATION_ROUTES);

/**
 * Normaliza o caminho para a comparação: `/alerts/` e `/alerts` são a mesma
 * tela, e o navegador produz as duas formas (link colado, redirect, atalho
 * salvo). A raiz é o caso que a remoção sozinha estragaria — sem o retorno
 * explícito, `/` viraria string vazia e o Mapa deixaria de ser destino.
 */
function normalizePathname(pathname: string): string {
  const withoutTrailingSlash = pathname.replace(/\/+$/, '');

  return withoutTrailingSlash === '' ? '/' : withoutTrailingSlash;
}

/**
 * Diz se o caminho é uma tela-destino — ou seja, se navegar até ele deve
 * **substituir** a entrada de histórico em vez de empilhar.
 *
 * A comparação é de caminho inteiro, e não prefixo, justamente para que
 * `/institutions/abc` continue sendo detalhe. Rota desconhecida (404) também
 * não é destino: ela é um beco, e engolir o "voltar" de quem chegou por link
 * velho seria o pior momento possível para fazer isso.
 */
export function isDestinationRoute(
  pathname: string,
  { ownedInstitutionId }: NavigationContext = {},
): boolean {
  const path = normalizePathname(pathname);

  // Destinos dinâmicos da conta: a aba "Operações" do QG e o "Minha instituição"
  // da dona. Têm o id na rota, então não cabem na lista fixa; sem isto empilhariam
  // histórico como uma tela de detalhe qualquer.
  if (OPERATION_ROUTE_PATTERN.test(path)) return true;
  if (ownedInstitutionId && path === `/institutions/${ownedInstitutionId}`) return true;

  return DESTINATION_ROUTE_SET.has(path);
}

/**
 * Rotas que se alcançam **pelo menu** — as que acendem a célula "Menu" da barra.
 *
 * Os condicionais entram sempre, pelo mesmo motivo de `DESTINATION_ROUTES`: o
 * que a bandeira e o `is_platform_admin()` decidem é quem vê a porta, não a que
 * superfície a tela pertence.
 */
const MENU_ROUTES: readonly string[] = [
  ...MODULE_MENU_ITEMS.map((item) => item.to),
  PLAYGROUND_MENU_ITEM.to,
  PLATFORM_MENU_ITEM.to,
];

/**
 * Diz se a tela atual pertence ao menu, para a célula "Menu" da barra ficar
 * ativa como uma aba. Sem isso, quem abria Doações pelo menu via a barra
 * inteira apagada e perdia a referência de onde estava.
 *
 * Aqui a comparação é por **prefixo de segmento**, ao contrário de
 * `isDestinationRoute`: é o mesmo critério do `NavLink` das outras abas, onde
 * `/alerts/abc` mantém Avisos acesa. `/institutions/abc` continua sendo do menu;
 * `/institution` (sem o "s") não é.
 */
export function isMenuRoute(
  pathname: string,
  { hqInstitutionId }: NavigationContext = {},
): boolean {
  const path = normalizePathname(pathname);

  // Na conta de QG, "Meus pedidos SOS" mora no menu (a barra ganhou o atalho do
  // mapa da operação no lugar de "Pedidos"), então `/requests` acende o Menu.
  if (hqInstitutionId && (path === REQUESTS_ROUTE || path.startsWith(`${REQUESTS_ROUTE}/`))) {
    return true;
  }

  // O mapa da operação é a aba "Operações" da barra do QG (`getNavigationItems`),
  // e não parte do menu: sem esta saída, `/institutions/:id/operation` acenderia
  // as duas células ao mesmo tempo pelo prefixo de `/institutions`.
  if (OPERATION_ROUTE_PATTERN.test(path)) return false;

  return MENU_ROUTES.some((route) => path === route || path.startsWith(`${route}/`));
}
